import { useState, Fragment } from 'react';
import { Crown, QrCode, RotateCcw, Trophy, Route, X, ListTree, AlertTriangle, ChevronDown, GitBranch, ArrowDown } from 'lucide-react';
import { TeamLogo } from './TeamLogo';
import MedalBadge from './MedalBadge';

const GHOST_MIN = 45; // a live match past this many minutes gets the "still live?" nudge
function elapsedMin(startedAt) {
  if (!startedAt) return null;
  const t = new Date(startedAt).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.round((Date.now() - t) / 60000));
}

// Layout constants
const BOX_W = 190;
const BOX_H = 66;
const GAP_Y = 22;
const COL_GAP = 56;
const SLOT = BOX_H + GAP_Y;
const HEADER = 26; // round-label band above the tree
const LINE = '#3A4150';
const LINE_HL = '#FFC24B'; // path-trace connector
const GOLD = '#FFC24B';
const BRONZE_GAP = 36;

const STATUS = {
  live: { c: '#E63946', l: 'LIVE' }, completed: { c: '#34D399', l: '' },
  bye: { c: '#64748B', l: 'BYE' }, pending: { c: '#64748B', l: '' },
  cancelled: { c: '#475569', l: '' },
};

// Named rounds by distance to the final (correct at 8/16/24/32 teams).
export function roundLabel(round, numRounds) {
  const dist = numRounds - round; // round is 1-based; final = numRounds
  if (dist === 0) return 'Final';
  if (dist === 1) return 'Semifinals';
  if (dist === 2) return 'Quarterfinals';
  return `Round ${round}`;
}

// Path-to-the-final: which matches (and won-forward edges) a team travels through.
// Reusable for any binary-tree bracket (SE, DE winners side, pool→knockout).
export function teamPath(flatMatches, teamId) {
  const boxes = new Set(); const edges = new Set();
  if (!teamId) return { boxes, edges };
  for (const m of flatMatches) {
    const side = m.team_a_id === teamId ? 'a' : (m.team_b_id === teamId ? 'b' : null);
    if (!side) continue;
    boxes.add(m.match_number);
    if (m.status === 'completed' && m.winner_side === side && m.next_match_number) {
      edges.add(m.match_number);            // the "step forward" connector
      boxes.add(m.next_match_number);        // the match they advanced into
    }
  }
  return { boxes, edges };
}

function BracketBox({ m, x, top, onMatch, onView, logoMap, onLogo, onReopen, onShowQR,
                     seedMap, onTrace, traceId, traceActive }) {
  const st = STATUS[m.status] || STATUS.pending;
  const live = m.status === 'live';
  const onPath = traceActive && (m.team_a_id === traceId || m.team_b_id === traceId);
  const dimmed = traceActive && !onPath;
  const actionable = onMatch && m.team_a_id && m.team_b_id && m.status !== 'completed' && m.status !== 'bye' && m.status !== 'cancelled';
  const viewable = !actionable && onView && (m.status === 'completed' || m.status === 'live' || m.status === 'bye');
  const handleClick = actionable ? () => onMatch(m) : (viewable ? () => onView(m) : undefined);
  const canQR = onShowQR && m.qr_token && m.team_a_id && m.team_b_id && m.status !== 'completed' && m.status !== 'bye' && m.status !== 'cancelled';
  const isBye = m.status === 'bye';
  const scoreFor = (side) => {
    if (m.status === 'pending' || m.status === 'bye') return '';
    if (live) return side === 'a' ? m.score_a : m.score_b; // live = current game score
    return m.games_won_a != null ? (side === 'a' ? m.games_won_a : m.games_won_b) : '';
  };
  const Row = ({ side, name, teamId }) => {
    const isWin = m.winner_side === side;
    const isLoser = m.status === 'completed' && m.winner_side && m.winner_side !== side && name;
    // Neutral-name law: names stay white; losers DIM (no red, no strikethrough).
    const logo = logoMap && teamId ? logoMap[teamId] : null;
    const seed = seedMap && teamId ? seedMap[teamId] : null;
    const traceable = onTrace && teamId && name && !isBye;
    const isTraced = traceId && teamId === traceId;
    return (
      <div className="flex items-center justify-between px-2.5" style={{ height: (BOX_H - 4) / 2, opacity: isLoser ? 0.5 : 1 }}>
        <span
          onClick={traceable ? (e) => { e.stopPropagation(); onTrace(teamId); } : undefined}
          className="flex items-center gap-1.5 truncate" style={{ maxWidth: BOX_W - 42, cursor: traceable ? 'pointer' : 'inherit' }}>
          {seed != null && <span className="tabular-nums" style={{ fontSize: 9, fontWeight: 700, color: '#6B7280', fontFamily: 'Oswald, sans-serif', width: 13, flex: 'none', textAlign: 'center' }}>{seed}</span>}
          {(logo || name) && !isBye && <TeamLogo logo={logo} name={name} size={22} onClick={onLogo} />}
          <span className="text-sm font-semibold truncate" style={{ color: name ? '#F1F5F9' : '#5B6472', fontStyle: isBye && !name ? 'italic' : 'normal' }}>
            {isWin && <Crown className="inline h-3 w-3 mr-0.5" style={{ color: GOLD }} />}
            {name || (isBye ? '— bye —' : 'TBD')}
          </span>
          {traceable && <Route style={{ width: 10, height: 10, flex: 'none', color: isTraced ? GOLD : '#454B57' }} />}
        </span>
        {isWin ? (
          <span className="tabular-nums" style={{ fontSize: 11, fontWeight: 700, color: '#3A2A00', background: GOLD, borderRadius: 5, padding: '1px 7px', fontFamily: 'Oswald, sans-serif' }}>{scoreFor(side)}</span>
        ) : (
          <span className="text-sm font-bold tabular-nums" style={{ color: live ? '#fff' : '#CBD5E1' }}>{scoreFor(side)}</span>
        )}
      </div>
    );
  };
  const border = live ? '#E63946' : onPath ? GOLD : '#2A2F37';
  const bside = `1px ${isBye ? 'dashed' : 'solid'} ${border}`;   // per-side (no shorthand `border` — avoids React shorthand/longhand warning)
  const leftBorder = (m.winner_side && !live && !isBye) ? `3px solid ${GOLD}` : bside;
  return (
    <div onClick={handleClick}
      data-testid={`bracket-box-${m.match_number}`}
      className="absolute rounded-lg overflow-hidden transition-all" style={{
        left: x, top, width: BOX_W, height: BOX_H,
        background: isBye ? '#12141A' : live ? '#1B1216' : '#15171C',
        borderTop: bside, borderRight: bside, borderBottom: bside, borderLeft: leftBorder,
        boxShadow: live ? '0 0 22px rgba(230,57,70,0.28)' : onPath ? `0 0 0 1px ${GOLD}, 0 0 16px rgba(255,194,75,0.22)` : 'none',
        opacity: dimmed ? 0.32 : 1,
        cursor: handleClick ? 'pointer' : 'default',
      }}>
      <Row side="a" name={m.team_a_name} teamId={m.team_a_id} />
      <div style={{ height: 1, background: '#23272F' }} />
      <Row side="b" name={m.team_b_name} teamId={m.team_b_id} />
      {onReopen && m.status === 'completed' && (
        <button data-testid={`reopen-match-${m.match_number}`} title="Correct this result"
          onClick={(e) => { e.stopPropagation(); onReopen(m); }}
          className="absolute flex items-center justify-center rounded-md"
          style={{ top: 3, right: 3, width: 20, height: 20, background: '#1A1A1A', border: '1px solid #3A3A3A', color: '#F59E0B' }}>
          <RotateCcw style={{ width: 11, height: 11 }} />
        </button>
      )}
      {canQR && (
        <button data-testid={`bracket-qr-${m.match_number}`} title="Show scorer QR"
          onClick={(e) => { e.stopPropagation(); onShowQR(m); }}
          className="absolute flex items-center justify-center rounded-md"
          style={{ top: 3, right: 3, width: 20, height: 20, background: '#1A0A0C', border: '1px solid #E6394655', color: '#E63946' }}>
          <QrCode style={{ width: 11, height: 11 }} />
        </button>
      )}
      {(st.l || m.court_name) && (
        <div className="absolute flex items-center gap-1" style={{ top: -16, left: 2, fontSize: 10, color: live ? '#E63946' : '#64748B', fontWeight: 700 }}>
          {live && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#E63946', boxShadow: '0 0 7px #E63946' }} className="animate-pulse" />}
          {st.l}{st.l && m.court_name ? ' · ' : ''}{m.court_name || ''}{actionable ? ' · tap to score' : (viewable ? ' · tap for history' : '')}
        </div>
      )}
    </div>
  );
}

// Renders a binary-tree bracket (single elim, or the winners side of double elim)
// Maps each winners-bracket match_number → the losers-bracket round its loser
// drops into (from loser_next_match_number). Only WB matches whose loser actually
// lands in the losers bracket get an entry (the WB final loser, who goes to a
// final, is skipped). Feeds the "drops to losers" indicator on the DE bracket.
export function buildDropMap(matches) {
  const byNum = {};
  matches.forEach(m => { byNum[m.match_number] = m; });
  const lbMax = Math.max(0, ...matches.filter(m => m.bracket_type === 'losers').map(m => m.round || 0));
  const map = {};
  matches.forEach(m => {
    if (m.bracket_type !== 'winners' || !m.loser_next_match_number) return;
    const tgt = byNum[m.loser_next_match_number];
    if (!tgt || tgt.bracket_type !== 'losers') return;
    const rd = tgt.round || 0;
    map[m.match_number] = rd >= lbMax ? 'Losers Final' : `Losers R${rd}`;
  });
  return map;
}

export default function TournamentBracket({ rounds, champion, championId, bronzeMatch, showChampion = true, onMatch, onView, logoMap, onLogo, onReopen, onShowQR, seedMap, dropMap }) {
  const [traceId, setTraceId] = useState(null);
  if (!rounds || rounds.length === 0 || rounds[0].length === 0) return null;
  const numRounds = rounds.length;
  const N = rounds[0].length;
  const champW = 150;
  const traceActive = !!traceId;
  const onTrace = (id) => setTraceId(prev => (prev === id ? null : id));
  // A match is on the traced team's route when the team plays in it (robust —
  // needs no next-match links; matches the box spotlight exactly).
  const onP = (m) => traceActive && m && (m.team_a_id === traceId || m.team_b_id === traceId);
  const champOnPath = traceActive && championId && championId === traceId;
  const flatMatches = rounds.flat().concat(bronzeMatch ? [bronzeMatch] : []);
  const tracedName = traceId ? (flatMatches.reduce((acc, m) =>
    acc || (m.team_a_id === traceId ? m.team_a_name : m.team_b_id === traceId ? m.team_b_name : null), null)) : null;
  const canBreakdown = !!(onView || onMatch); // is the "tap the score" action available?

  const centerY = (rr, j) => HEADER + (j + 0.5) * Math.pow(2, rr) * SLOT;
  const colX = (rr) => rr * (BOX_W + COL_GAP);

  const finalCenterY = centerY(numRounds - 1, 0);
  const bronzeTop = bronzeMatch ? finalCenterY + BOX_H / 2 + BRONZE_GAP : 0;
  const bronzeContentH = bronzeMatch ? bronzeTop + BOX_H + 12 : 0;
  const totalH = Math.max(N * SLOT + HEADER, bronzeContentH) + (dropMap ? 14 : 0); // room for drop labels
  const totalW = numRounds * (BOX_W + COL_GAP) + (showChampion ? champW : 0);

  const segs = [];  // {left, top, w, h, hot}
  const addH = (x, y, w, hot) => segs.push({ left: x, top: y - 1, w, h: 2, hot });
  const addV = (x, y0, y1, hot) => segs.push({ left: x - 1, top: Math.min(y0, y1), w: 2, h: Math.abs(y1 - y0), hot });
  for (let rr = 0; rr < numRounds - 1; rr++) {
    const parents = rounds[rr + 1].length;
    for (let p = 0; p < parents; p++) {
      const cA = rounds[rr][2 * p], cB = rounds[rr][2 * p + 1];
      const parent = rounds[rr + 1][p];
      const c0 = centerY(rr, 2 * p), c1 = centerY(rr, 2 * p + 1);
      const childRight = colX(rr) + BOX_W;
      const parentLeft = colX(rr + 1);
      const midX = (childRight + parentLeft) / 2;
      // A connector is "hot" when the traced team travels it: on-path child → on-path parent.
      const pOn = onP(parent);
      addH(childRight, c0, midX - childRight, onP(cA) && pOn);
      addH(childRight, c1, midX - childRight, onP(cB) && pOn);
      const parentHot = (onP(cA) || onP(cB)) && pOn;
      addV(midX, c0, c1, parentHot);
      addH(midX, centerY(rr + 1, p), parentLeft - midX, parentHot);
    }
  }
  if (showChampion) {
    const lastX = colX(numRounds - 1) + BOX_W;
    const champX = colX(numRounds);
    const fin = rounds[numRounds - 1][0];
    addH(lastX, centerY(numRounds - 1, 0), champX - lastX, champOnPath && onP(fin));
  }

  return (
    <div>
      {/* Active-trace chip — who's lit + how to clear (doesn't scroll with the tree) */}
      {traceId && (
        <button data-testid="bracket-trace-chip" onClick={() => setTraceId(null)}
          className="inline-flex items-center gap-2 mb-2" style={{ background: 'rgba(255,194,75,0.12)', border: `1px solid ${GOLD}80`, borderRadius: 999, padding: '5px 8px 5px 12px', cursor: 'pointer' }}>
          <Route style={{ width: 13, height: 13, color: GOLD }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: GOLD, fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', letterSpacing: 1 }}>Tracing {tracedName}</span>
          <span style={{ width: 16, height: 16, borderRadius: '50%', background: 'rgba(255,194,75,0.25)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><X style={{ width: 10, height: 10, color: GOLD }} /></span>
        </button>
      )}
      <div className="overflow-x-auto pb-3" style={{ scrollSnapType: 'x proximity' }}>
      <div className="relative" onClick={() => traceActive && setTraceId(null)}
        style={{ width: totalW, height: totalH, minWidth: totalW }}>
        {/* Round-label headers */}
        {rounds.map((round, rr) => (
          <div key={`h${rr}`} className="absolute" style={{ left: colX(rr), top: 2, width: BOX_W, scrollSnapAlign: 'start' }}>
            <p className="text-[10px] font-bold uppercase" style={{ color: '#8A93A3', letterSpacing: 2, fontFamily: 'Oswald, sans-serif' }}>{roundLabel(round[0].round, numRounds)}</p>
          </div>
        ))}
        {segs.map((s, i) => (
          <div key={i} style={{ position: 'absolute', left: s.left, top: s.top, width: s.w, height: s.h, background: s.hot ? LINE_HL : LINE, opacity: traceActive && !s.hot ? 0.3 : 1, boxShadow: s.hot ? `0 0 6px ${LINE_HL}` : 'none' }} />
        ))}
        {rounds.map((round, rr) => round.map((m, j) => {
          const drop = dropMap && dropMap[m.match_number];
          return (
            <Fragment key={m.id}>
              <BracketBox m={m} x={colX(rr)} top={centerY(rr, j) - BOX_H / 2}
                onMatch={onMatch} onView={onView} logoMap={logoMap} onLogo={onLogo} onReopen={onReopen} onShowQR={onShowQR}
                seedMap={seedMap} onTrace={onTrace} traceId={traceId} traceActive={traceActive} />
              {drop && !traceActive && (
                <div className="absolute flex items-center gap-1" data-testid={`drop-${m.match_number}`}
                  style={{ left: colX(rr) + 6, top: centerY(rr, j) + BOX_H / 2 + 3, fontSize: 9, fontWeight: 700, letterSpacing: '0.02em', pointerEvents: 'none' }}>
                  <ArrowDown style={{ width: 10, height: 10, color: '#F59E0B', flex: 'none' }} />
                  <span style={{ color: '#B08A4A' }}>Loser → <span style={{ color: '#F59E0B' }}>{drop}</span></span>
                </div>
              )}
            </Fragment>
          );
        }))}
        {showChampion && (
          <div className="absolute rounded-lg flex flex-col items-center justify-center px-3" style={{
            left: colX(numRounds), top: centerY(numRounds - 1, 0) - 28, width: champW, height: 56,
            background: champion ? 'linear-gradient(135deg,#FFD60A,#FFB800)' : '#15171C',
            border: champion ? 'none' : '1px dashed #2E333C',
            boxShadow: champion ? '0 0 22px rgba(255,214,10,0.3)' : 'none',
            opacity: traceActive && !champOnPath ? 0.32 : 1,
          }}>
            {!champion && <Trophy style={{ width: 16, height: 16, color: '#4B515C' }} />}
            <span className="text-xs font-black uppercase tracking-wider" style={{ color: champion ? '#1A1A1A' : '#5B6472', fontFamily: 'Oswald, sans-serif' }}>Champion</span>
            {champion && <span className="text-sm font-bold truncate max-w-full" style={{ color: '#1A1A1A' }}>{champion}</span>}
          </div>
        )}

        {/* 3rd Place Match — integrated below the Final, same column */}
        {bronzeMatch && (
          <>
            <div style={{
              position: 'absolute', left: colX(numRounds - 1) + BOX_W / 2,
              top: finalCenterY + BOX_H / 2, width: 0, height: BRONZE_GAP - 14,
              borderLeft: '2px dashed rgba(224,168,107,0.45)',
            }} />
            <div className="absolute flex items-center gap-1.5" style={{ left: colX(numRounds - 1), top: bronzeTop - 20 }}>
              <MedalBadge rank={3} size={16} />
              <span style={{ fontSize: 9, color: '#CD7F32', fontWeight: 800, letterSpacing: 1.5, textTransform: 'uppercase', fontFamily: 'Oswald, sans-serif' }}>3rd Place</span>
            </div>
            <BracketBox m={bronzeMatch} x={colX(numRounds - 1)} top={bronzeTop}
              onMatch={onMatch} onView={onView} logoMap={logoMap} onLogo={onLogo} onReopen={onReopen} onShowQR={onShowQR}
              seedMap={seedMap} onTrace={onTrace} traceId={traceId} traceActive={traceActive} />
          </>
        )}
      </div>
      </div>
      {/* Legend — states the tap rule so trace vs breakdown is never a guess */}
      <div className="flex items-center gap-x-4 gap-y-1 flex-wrap" style={{ marginTop: 2 }}>
        <span className="inline-flex items-center gap-1.5" style={{ fontSize: 11, color: '#8A93A3' }}>
          <Route style={{ width: 13, height: 13, color: GOLD }} />Tap a <b style={{ color: '#C9CDD4', fontWeight: 700 }}>team</b> to trace their path
        </span>
        {canBreakdown && (
          <span className="inline-flex items-center gap-1.5" style={{ fontSize: 11, color: '#8A93A3' }}>
            <ListTree style={{ width: 13, height: 13, color: '#8A93A3' }} />Tap the <b style={{ color: '#C9CDD4', fontWeight: 700 }}>score</b> for the game breakdown
          </span>
        )}
      </div>
    </div>
  );
}

// Column layout (no connectors) — for the losers bracket of double elim.
export function ColumnsView({ matches, onMatch, onView, logoMap, onLogo, color = '#F59E0B', onReopen, onShowQR, seedMap }) {
  const rounds = groupRounds(matches);
  if (matches.length === 0) return null;
  const numRounds = rounds.length;
  return (
    <div className="flex gap-10 overflow-x-auto pb-3" style={{ paddingTop: 18 }}>
      {rounds.map((round, rr) => (
        <div key={rr} className="shrink-0" style={{ width: BOX_W }}>
          <p className="text-[10px] mb-2 font-bold uppercase" style={{ color: '#8A93A3', letterSpacing: 2, fontFamily: 'Oswald, sans-serif' }}>Losers · Round {round[0].round}</p>
          <div className="relative" style={{ height: round.length * SLOT }}>
            {round.map((m, j) => (
              <BracketBox key={m.id} m={m} x={0} top={j * SLOT} onMatch={onMatch} onView={onView} logoMap={logoMap} onLogo={onLogo} onReopen={onReopen} onShowQR={onShowQR} seedMap={seedMap} traceActive={false} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Contained matchup layout for the list (not MatchupCard, which is built for
// full-width broadcast panels and stretches). Avatars carry side colour; a team
// LOGO shows when uploaded, else coloured initials as the placeholder.
const A_COL = '#E63946', B_COL = '#3B82F6';
function seqInitials(name) {
  const w = String(name || '').trim().split(/\s+/);
  return ((w[0]?.[0] || '') + (w[1]?.[0] || '')).toUpperCase() || '?';
}
function TeamAvatar({ name, logo, color, size = 24 }) {
  if (logo) return <img src={logo} alt="" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', border: `1px solid ${color}66`, flex: 'none' }} />;
  return (
    <span style={{ width: size, height: size, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Oswald, sans-serif', fontWeight: 900, fontSize: Math.round(size * 0.37), background: `${color}20`, color, border: `1px solid ${color}66`, flex: 'none' }}>{seqInitials(name)}</span>
  );
}
// side 'a' = right team (name outward, avatar inward toward centre); 'b' = mirror.
function SeqSide({ name, logo, color, side, dim, winner, big }) {
  const isA = side === 'a';
  // Both sides pack toward the centre VS: side b (row) uses flex-start; side a is
  // row-reverse (its main-start is the right edge), so flex-start hugs the VS there
  // too. flex-end on side a used to push the left team to the outer edge (asymmetric).
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0, justifyContent: 'flex-start', flexDirection: isA ? 'row-reverse' : 'row', opacity: dim ? 0.5 : 1 }}>
      <TeamAvatar name={name} logo={logo} color={color} size={big ? 26 : 24} />
      {big && <span aria-hidden="true" style={{ width: 3, height: 26, borderRadius: 3, background: color, flex: 'none' }} />}
      <span className="truncate" style={{ fontSize: 14, fontWeight: 600, color: name ? '#F1F5F9' : '#5B6472', textAlign: isA ? 'right' : 'left' }}>
        {winner && <Crown className="inline h-3 w-3 mr-0.5" style={{ color: GOLD }} />}{name || 'TBD'}
      </span>
    </div>
  );
}
function VsDiamond() {
  return (
    <span style={{ width: 28, height: 28, transform: 'rotate(45deg)', background: '#0D0D0D', border: '1px solid #333', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
      <span style={{ transform: 'rotate(-45deg)', fontFamily: 'Oswald, sans-serif', fontWeight: 900, fontSize: 9, color: '#525252' }}>VS</span>
    </span>
  );
}

// Bracket sections for the two-level sequence dividers. Drawn lucide icons only
// (no emoji, per design law); colors match the bracket-view section semantics
// (green winners / amber losers / gold finals).
const SEQ_SECTIONS = {
  winners: { label: 'Winners bracket', color: '#34D399', Icon: GitBranch },
  losers:  { label: 'Losers bracket',  color: '#F59E0B', Icon: ArrowDown },
  finals:  { label: 'Finals',          color: '#FFD54A', Icon: Trophy },
};
// Section headers (Winners/Losers/Finals) only make sense with a losers bracket,
// i.e. double elim. Single elim collapses to one flow of named rounds (no header),
// and its championship match reads "Final", never "Grand final".
function seqSection(m, isDouble) {
  if (!isDouble) return m.bracket_type === 'third_place' ? 'third' : 'main';
  const bt = m.bracket_type;
  if (bt === 'losers') return 'losers';
  if (bt === 'final' || bt === 'final_reset' || bt === 'third_place') return 'finals';
  return 'winners';
}
function seqRoundLabel(m, mainMax, isDouble) {
  if (!isDouble) {
    if (m.bracket_type === 'third_place') return '3rd place';
    return roundLabel(m.round, mainMax); // Round N / QF / SF / Final
  }
  const bt = m.bracket_type;
  if (bt === 'final_reset') return 'Grand final · reset';
  if (bt === 'third_place') return '3rd place';
  if (bt === 'final') return 'Grand final';
  if (bt === 'losers') return `Round ${m.round}`;
  return roundLabel(m.round, mainMax);
}

// Departures-board match list — adapts open play's match queue to tournaments
// (checklist 2.0/2.3). Three bands: NOW PLAYING (live, court or floating + ghost
// flag) · MATCH SEQUENCE (played in match_number order = winners→losers→final,
// jump to any ready match via its QR) · RESULTS (collapsed). Pure presentation
// over the existing matches — no ordering/engine logic changes. Capped width so
// it never stretches sparse on wide screens.
export function MatchListView({ matches, onMatch, onView, logoMap, onLogo, onShowQR, onReopen }) {
  const [showResults, setShowResults] = useState(false);
  const visible = matches.filter(m => m.status !== 'cancelled');
  const byNum = (a, b) => a.match_number - b.match_number;
  const live = visible.filter(m => m.status === 'live').sort(byNum);
  const upcoming = visible.filter(m => !['live', 'completed', 'bye'].includes(m.status)).sort(byNum);
  const done = visible.filter(m => m.status === 'completed' || m.status === 'bye').sort(byNum);

  // Two-level sequence dividers: a bracket SECTION header (winners/losers/finals,
  // drawn icon — no emoji) with named ROUNDS beneath (roundLabel → QF/SF/Final).
  // Section headers show only when >1 section exists (double-elim); single-elim
  // degrades to just the named rounds. winnersMax feeds roundLabel's distance math.
  const isDouble = visible.some(m => m.bracket_type === 'losers');
  const mainMatches = visible.filter(m => isDouble ? m.bracket_type === 'winners' : m.bracket_type !== 'third_place');
  const mainMax = Math.max(1, ...mainMatches.map(m => m.round || 1));
  const multiSection = isDouble; // section headers only for double elim
  const logoOf = (id) => (logoMap && id ? logoMap[id] : null);
  const ready = (m) => !!(m.team_a_id && m.team_b_id);
  const firstReadyNum = upcoming.find(ready)?.match_number;
  const Section = ({ children }) => (
    <div className="rounded-2xl overflow-hidden" style={{ background: '#141414', border: '1px solid #262626' }}>{children}</div>
  );

  return (
    <div className="space-y-3" data-testid="tournament-sequence-board" style={{ maxWidth: 680, margin: '0 auto' }}>
      {/* ── NOW PLAYING ── */}
      {live.length > 0 && (
        <Section>
          <div className="px-4 py-3" style={{ background: '#1C1C1C', borderBottom: '1px solid #1A1A1A' }}>
            <span className="text-sm font-black uppercase" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>Now playing</span>
          </div>
          <div className="p-3 space-y-2">
            {live.map(m => {
              const mins = elapsedMin(m.started_at);
              const ghost = mins != null && mins >= GHOST_MIN;
              const clickable = onMatch || onView;
              return (
                <div key={m.id} data-testid={`live-match-${m.match_number}`}
                  onClick={clickable ? () => (onMatch ? onMatch(m) : onView(m)) : undefined}
                  className="rounded-xl" style={{ background: 'rgba(230,57,70,0.07)', border: '1px solid #E6394630', cursor: clickable ? 'pointer' : 'default', padding: '12px 16px' }}>
                  <div className="flex items-center gap-2 text-[10px] font-extrabold uppercase tracking-wider" style={{ marginBottom: ghost ? 6 : 12 }}>
                    <span className="inline-flex items-center gap-1.5" style={{ color: '#E63946' }}>
                      <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: '#E63946', boxShadow: '0 0 7px #E63946' }} />
                      Live · {m.court_name || 'Court TBA'}{mins != null ? ` · ${mins} min` : ''}
                    </span>
                    <span className="ml-auto" style={{ color: '#525252' }}>Match {m.match_number}</span>
                  </div>
                  {ghost && (
                    <p className="text-[10px] font-bold uppercase" style={{ color: '#F59E0B', marginBottom: 10 }}>
                      <AlertTriangle className="inline h-3 w-3 mr-1" />Still live? — {mins} min · tap to finalize
                    </p>
                  )}
                  <div className="flex items-center justify-center" style={{ gap: 18 }}>
                    <SeqSide side="a" big name={m.team_a_name} logo={logoOf(m.team_a_id)} color={A_COL} />
                    <span className="flex items-baseline" style={{ gap: 10, background: '#000', border: '1px solid #333', borderRadius: 14, padding: '7px 18px', fontFamily: 'Oswald, sans-serif', fontVariantNumeric: 'tabular-nums', flex: 'none' }}>
                      <span style={{ fontSize: 32, fontWeight: 900, color: A_COL }}>{m.score_a ?? 0}</span>
                      <span style={{ fontSize: 20, color: '#3A3A3A' }}>—</span>
                      <span style={{ fontSize: 32, fontWeight: 900, color: B_COL }}>{m.score_b ?? 0}</span>
                    </span>
                    <SeqSide side="b" big name={m.team_b_name} logo={logoOf(m.team_b_id)} color={B_COL} />
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* ── MATCH SEQUENCE ── */}
      <Section>
        <div className="flex items-center gap-2 px-4 py-3" style={{ background: '#1C1C1C', borderBottom: '1px solid #1A1A1A' }}>
          <span className="text-sm font-black uppercase" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>Match sequence</span>
          <span className="text-[10px] uppercase ml-auto text-right" style={{ color: '#555' }}>played in order · jump to any ready match</span>
        </div>
        {upcoming.length === 0 ? (
          <div className="p-6 text-center">
            <p className="text-sm" style={{ color: '#444' }}>Every match is in play or done</p>
          </div>
        ) : (
          upcoming.map((m, i) => {
            const prev = i > 0 ? upcoming[i - 1] : null;
            const sec = seqSection(m, isDouble);
            const roundText = seqRoundLabel(m, mainMax, isDouble);
            const newSection = multiSection && (!prev || seqSection(prev, isDouble) !== sec);
            const newRound = !prev || seqRoundLabel(prev, mainMax, isDouble) !== roundText || newSection;
            const isReady = ready(m);
            const isNextUp = m.match_number === firstReadyNum;
            const canQR = onShowQR && m.qr_token && isReady;
            const S = SEQ_SECTIONS[sec];
            return (
              <Fragment key={m.id}>
                {newSection && (
                  <>
                    <div className="flex items-center gap-2.5 px-4 pt-3 pb-1.5" data-testid={`seq-section-${sec}`} style={{ borderTop: prev ? '1px solid #1A1A1A' : 'none' }}>
                      <S.Icon style={{ width: 15, height: 15, color: S.color, flex: 'none' }} />
                      <span className="text-[12.5px] font-black uppercase" style={{ fontFamily: 'Oswald, sans-serif', letterSpacing: '0.08em', color: S.color }}>{S.label}</span>
                    </div>
                    <div style={{ height: 2, margin: '0 16px 2px', borderRadius: 2, background: `linear-gradient(90deg, ${S.color}55, transparent)` }} />
                  </>
                )}
                {newRound && (
                  <div className="flex items-center gap-3 px-4 pt-2.5 pb-1">
                    <div style={{ flex: 1, height: 1, background: '#1A1A1A' }} />
                    <span className="text-[9.5px] font-bold uppercase shrink-0" style={{ color: '#525252', letterSpacing: '0.14em' }}>{roundText}</span>
                    <div style={{ flex: 1, height: 1, background: '#1A1A1A' }} />
                  </div>
                )}
                <div className="grid items-center" data-testid={`seq-match-${m.match_number}`} style={{ gridTemplateColumns: '2.6rem minmax(0,1fr) 3rem', borderTop: !newRound ? '1px solid #1A1A1A' : 'none', background: isNextUp ? 'rgba(245,158,11,0.06)' : 'transparent', borderLeft: `3px solid ${isNextUp ? GOLD : 'transparent'}`, position: 'relative' }}>
                  {isNextUp && (
                    <span className="absolute font-black uppercase" style={{ top: 6, left: 14, fontSize: 8, letterSpacing: '0.12em', color: '#0A0A0A', background: GOLD, padding: '2px 7px', borderRadius: 5, fontFamily: 'Oswald, sans-serif' }}>NEXT UP</span>
                  )}
                  <span className="tabular-nums text-center" style={{ fontFamily: 'Oswald, sans-serif', fontWeight: 900, fontSize: 17, color: isNextUp ? GOLD : '#444', paddingLeft: 6 }}>{m.match_number}</span>
                  <div className="flex items-center justify-center" style={{ gap: 14, padding: isNextUp ? '20px 8px 14px' : '13px 8px' }}>
                    <SeqSide side="a" name={m.team_a_name} logo={logoOf(m.team_a_id)} color={A_COL} />
                    <VsDiamond />
                    <SeqSide side="b" name={m.team_b_name} logo={logoOf(m.team_b_id)} color={B_COL} />
                  </div>
                  <div className="flex items-center justify-center" style={{ paddingRight: 10 }}>
                    {canQR ? (
                      <button data-testid={`seq-qr-${m.match_number}`} title="Show scorer QR" onClick={(e) => { e.stopPropagation(); onShowQR(m); }}
                        className="rounded-lg flex items-center justify-center" style={{ width: 32, height: 32, background: isNextUp ? '#1A0A0C' : '#141414', border: `1px solid ${isNextUp ? '#E6394655' : '#2A2A2A'}`, color: isNextUp ? '#E63946' : '#777' }}>
                        <QrCode className="h-4 w-4" />
                      </button>
                    ) : (
                      <span className="text-[8.5px] text-center uppercase font-bold" style={{ color: isReady ? '#8A8A82' : '#4B515C', letterSpacing: '0.05em' }}>{isReady ? 'Ready' : 'Waiting'}</span>
                    )}
                  </div>
                </div>
              </Fragment>
            );
          })
        )}
      </Section>

      {/* ── RESULTS (collapsed) ── */}
      {done.length > 0 && (
        <div className="rounded-2xl overflow-hidden" style={{ background: '#0F0F0F', border: '1px solid #1E1E1E' }}>
          <button data-testid="results-toggle" onClick={() => setShowResults(s => !s)} className="flex items-center gap-2 w-full px-4 py-3">
            <Trophy className="h-4 w-4" style={{ color: GOLD }} />
            <span className="text-xs font-bold uppercase" style={{ color: '#8A8A82', letterSpacing: 1 }}>{done.length} result{done.length === 1 ? '' : 's'}</span>
            <ChevronDown className="h-4 w-4 ml-auto" style={{ color: '#555', transform: showResults ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
          </button>
          {showResults && (
            <div style={{ borderTop: '1px solid #1A1A1A' }}>
              {done.map((m, i) => {
                const w = m.winner_side;
                const games = m.games && m.games.length ? m.games : [{ score_a: m.score_a, score_b: m.score_b }];
                return (
                  <div key={m.id} data-testid={`result-match-${m.match_number}`} onClick={onView ? () => onView(m) : undefined}
                    className="grid items-center" style={{ gridTemplateColumns: '2.6rem minmax(0,1fr) 3rem', borderTop: i ? '1px solid #141414' : 'none', cursor: onView ? 'pointer' : 'default' }}>
                    <span className="tabular-nums text-center" style={{ fontFamily: 'Oswald, sans-serif', fontWeight: 900, fontSize: 14, color: '#3A3A3A', paddingLeft: 6 }}>{m.match_number}</span>
                    {m.status === 'bye' ? (
                      <p className="py-3 px-2 text-sm" style={{ color: '#5B6472' }}>{m.team_a_name || m.team_b_name} — bye</p>
                    ) : (
                      <div className="flex items-center justify-center" style={{ gap: 12, padding: '11px 8px' }}>
                        <SeqSide side="a" name={m.team_a_name} logo={logoOf(m.team_a_id)} color={A_COL} dim={w === 'b'} winner={w === 'a'} />
                        <span className="flex" style={{ gap: 4, flex: 'none' }}>
                          {games.map((g, gi) => (
                            <span key={gi} className="rounded-md" style={{ fontSize: 11, padding: '2px 6px', background: '#0D0D0D', border: '1px solid #2E2E2E', fontFamily: 'Oswald, sans-serif', fontVariantNumeric: 'tabular-nums' }}>
                              <span style={{ color: w === 'b' ? '#A3A3A3' : '#fff' }}>{g.score_a}</span><span style={{ color: '#525252' }}>–</span><span style={{ color: w === 'b' ? '#fff' : '#A3A3A3' }}>{g.score_b}</span>
                            </span>
                          ))}
                        </span>
                        <SeqSide side="b" name={m.team_b_name} logo={logoOf(m.team_b_id)} color={B_COL} dim={w === 'a'} winner={w === 'b'} />
                      </div>
                    )}
                    <div className="flex items-center justify-center" style={{ paddingRight: 10 }}>
                      {onReopen && m.status === 'completed' && (
                        <button data-testid={`reopen-match-${m.match_number}`} title="Correct this result" onClick={(e) => { e.stopPropagation(); onReopen(m); }}
                          className="rounded-lg flex items-center justify-center" style={{ width: 32, height: 32, background: '#141414', border: '1px solid #2A2A2A', color: '#F59E0B' }}>
                          <RotateCcw className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Group a flat list of bracket matches into rounds[] for a binary tree.
export function groupRounds(matches) {
  const byRound = {};
  matches.forEach(m => { (byRound[m.round] = byRound[m.round] || []).push(m); });
  return Object.keys(byRound).sort((a, b) => a - b)
    .map(r => byRound[r].sort((a, b) => a.match_number - b.match_number));
}
